import React from "react";
import AppContext from "@/js/common/AppContext";
import Button from "@/js/common/ui/Button";
import Util from "@/js/common/Util";
import TextBox from "@/js/common/ui/input/TextBox";
import SelectBox from "@/js/common/ui/input/SelectBox";
import DatePickerBox from "@/js/common/ui/input/DatePickerBox";
import NumberTextBox from "@/js/common/ui/input/NumberTextBox";
import Axios from "@/js/common/Axios";
import * as Atom from "@/js/common/Atom";
import { useRecoilValue } from "recoil";
import BudgetFile from "../budgetFileBox/BudgetFile";
import { budgetFilesProps } from "@/js/common/types/types";
import AtrzLineBox from "./dialog/AtrzLineBox";
import BgtOvrlDpstMngList from "./BgtOvrlDpstMngList";

const BgtOvrlDpstMng = () => {
    const userInfo = useRecoilValue(Atom.userInfo);

    // 검색조건
    const [upOgnzCd        , setUpOgnzCd        ] = React.useState<string>(userInfo.upOgnzCd);      // 상위조직코드(본부)
    const [ognzCd          , setOgnzCd          ] = React.useState<string>(userInfo.ognzCd  );      // 조직코드(지부)
    const [acntgYear       , setAcntgYear       ] = React.useState<string>(Util.getNowAcntgYear()); // 회계연도
    const [searchAtrzSttsCd, setSearchAtrzSttsCd] = React.useState<string>("");                     // 진행상태
    const [callTime        , setCallTime        ] = React.useState<Date>(new Date()); // 검색 트리거

    const [listLine  , setListLine  ] = React.useState<any[]>([]);  // 결재 라인 정보
    const [openLine  , setOpenLine  ] = React.useState<boolean>(false); // 기본 펼침/접힘

    // =========================
    // 좌측 입력폼(상세)
    // =========================
    const [ovrlMbsdsDpstDocId, setOvrlMbsdsDpstDocId] = React.useState<string>(""); // 총괄 교부금 문서ID(PK)
    const [dpstRsdocNo       , setDpstRsdocNo       ] = React.useState<string>(""); // 결의서 문서번호
    const [dpstDt            , setDpstDt            ] = React.useState<string>(""); // 수입일자(YYYY-MM-DD)
    const [bgtSbjct1stCd     , setBgtSbjct1stCd     ] = React.useState<string>("I_003"); // 관 코드(bgtSbjct1stCd)
    const [bgtSbjct2stCd     , setBgtSbjct2stCd     ] = React.useState<string>("I_003_001"); // 항 코드(bgtSbjct2stCd)
    const [bgtSbjct3stCd     , setBgtSbjct3stCd     ] = React.useState<string>(""); // 목 코드(bgtSbjct3stCd)
    const [abstrCn           , setAbstrCn           ] = React.useState<string>(""); // 적요(abstrCn)
    const [memo              , setMemo              ] = React.useState<string>(""); // 메모(memo)
    const [dpstAmt           , setDpstAmt           ] = React.useState<string>(""); // 수입금액(dpstAmt)
    const [atrzOpnnCn        , setAtrzOpnnCn        ] = React.useState<string>(""); // 반려사유(atrzOpnnCn)
    const [atrzSttsCd        , setAtrzSttsCd        ] = React.useState<string>("00"); // 결재상태정보

    const [budgetFiles     , setBudgetFiles     ] = React.useState<File[]>([]); // 저장할 파일
    const [savedBudgetFiles, setSavedBudgetFiles] = React.useState<budgetFilesProps[]>([]); // 저장된 파일 리스트

    // 초기화
    const doReset = () => {
        setOvrlMbsdsDpstDocId(""); // 수입결의서 문서번호
        setDpstRsdocNo       ("");
        setDpstDt            (""); // 수입일자
        setBgtSbjct1stCd("I_003"); // 1차 코드(관)
        setBgtSbjct2stCd("I_003_001"); // 2차 코드(항)
        setBgtSbjct3stCd(""); // 3차 코드(목)
        setAbstrCn      (""); // 적요
        setMemo         (""); // 메모
        setDpstAmt      (""); // 금액
        setAtrzOpnnCn   (""); // 반려사유
        setAtrzSttsCd   ("00"); // 상태정보
        // 첨부파일
        setBudgetFiles     ([]); // 저장할 파일
        setSavedBudgetFiles([]); // 저장된 파일
    };

    // 문서 ID 변경 시 드롭존 초기화
    React.useEffect(() => {
        setBudgetFiles([]);
    }, [ovrlMbsdsDpstDocId]);

    // 상세 조회(행 클릭)
    React.useEffect(() => {    
        if (!ovrlMbsdsDpstDocId) return;

        AppContext.showSpinner();
        Axios.dataAccess({
            url: "system/bgt/ovrl/dpst/select-ovrl-dpst-dtl.do",
            methodType: "post",
            paramType: "json",
            paramData: {
                ovrlMbsdsDpstDocId: ovrlMbsdsDpstDocId,
            },
            onSuccessFn: (res: any) => {
                const item = res.result.item;
                if (Util.isNotEmpty(item)) {
                    setOvrlMbsdsDpstDocId(item.ovrlMbsdsDpstDocId);
                    setDpstRsdocNo       (item.dpstRsdocNo       );
                    setDpstDt            (item.dpstDt            );
                    setBgtSbjct1stCd     (item.bgtSbjct1stCd     );
                    setBgtSbjct2stCd     (item.bgtSbjct2stCd     );
                    setBgtSbjct3stCd     (item.bgtSbjct3stCd     );
                    setAbstrCn           (item.abstrCn           );
                    setMemo              (item.memo              );
                    setDpstAmt           (item.dpstAmt           );
                    setAtrzOpnnCn        (item.atrzOpnnCn        );
                    setAtrzSttsCd        (item.atrzSttsCd        );
                    setListLine          (res.result.listLine    );  // 결재 라인리스트

                    setSavedBudgetFiles(res.result.listFile);
                }
                AppContext.hideSpinner();
            },
        });
    }, [ovrlMbsdsDpstDocId]);

    // 수입결의서 저장   
    function doSave(atrzSttsCd: string) {
        let msgText = "임시저장";

        // 수입일자 확인
        if (Util.isEmpty(dpstDt)) {
            AppContext.openAlert({
                title: "수입일자 확인",
                msg: "수입일자를 입력하세요.",
                icon: "warning"
            });
            return;
        }
        // 목 확인
        if (Util.isEmpty(bgtSbjct3stCd)) { 
            AppContext.openAlert({
                title: "예산과목 목 확인", 
                msg: "예산과목 목 항목을 선택하세요.", 
                icon: "warning" 
            }); 
            return; 
        }

        // 작성완료 필수입력 확인
        if (atrzSttsCd === "02") {
            if (Util.isEmpty(bgtSbjct3stCd)) { AppContext.openAlert({ title: "예산과목 목 확인", msg: "예산과목 목 항목을 선택하세요.", icon: "warning" }); return; }
            if (Util.isEmpty(abstrCn      )) { AppContext.openAlert({ title: "적요 확인"       , msg: "적요를 입력하세요."            , icon: "warning" }); return; }
            if (Util.isEmpty(dpstAmt      )) { AppContext.openAlert({ title: "금액 확인"       , msg: "금액을 입력하세요."            , icon: "warning" }); return; }
            msgText = "작성완료";
        }

        AppContext.openAlert({
            title: "확인",
            msg: "수입결의서를 " + msgText + "하시겠습니까?",
            icon: "question",
            closeText: "취소",
            confirmText: msgText,
            handleConfirm: () => {
                AppContext.showSpinner();
                Axios.dataAccess({
                    url: "system/bgt/ovrl/dpst/save-ovrl-dpst.do",
                    methodType: "post",
                    isMultipart: true,
                    paramData: {
                        ovrlMbsdsDpstDocId: ovrlMbsdsDpstDocId , // 수입결의서 문서번호
                        orgCd             : ognzCd             , // 조직코드
                        dpstDt            : dpstDt             , // 수입일자 (YYYYMMDD)
                        acntgYear         : Util.getAcntgYear(dpstDt), // 회계연도                        
                        bgtSbjct1stCd     : bgtSbjct1stCd      , // 1차 코드(관)
                        bgtSbjct2stCd     : bgtSbjct2stCd      , // 2차 코드(항)
                        bgtSbjct3stCd     : bgtSbjct3stCd      , // 3차 코드(목)
                        abstrCn           : abstrCn            , // 적요
                        memo              : memo               , // 메모
                        dpstAmt           : Util.uncomma(dpstAmt) === "" ? 0 : Number(Util.uncomma(dpstAmt)), // 수입 금액
                        atrzSttsCd        : atrzSttsCd         , // 상태정보
                        budgetFiles       : budgetFiles        , // 첨부파일
                        fileSeCd          : "C01"              , // 파일구분코드(총괄 수입[C01])
                    },
                    onSuccessFn: (res: any) => {
                    AppContext.hideSpinner();
                    AppContext.openAlert({
                        title: "수입결의서 저장",
                        msg: "수입결의서가 저장되었습니다.",
                        icon: "check"
                    });
                    // 신규 저장 시 문서 ID 세팅
                    if (Util.isEmpty(ovrlMbsdsDpstDocId)) {
                        setOvrlMbsdsDpstDocId(res.result.ovrlMbsdsDpstDocId);
                    }

                    if (atrzSttsCd === "02") {
                        doReset();
                    } else {
                        setAtrzSttsCd(atrzSttsCd);
                    }

                    setCallTime(new Date());
                },
                });
            },
        });
    }

    // 삭제
    function doDelete() {
        AppContext.openAlert({
            title: "확인",
            msg: "수입결의서를 삭제하시겠습니까?",
            icon: "question",
            closeText: "취소",
            confirmText: "삭제",
            handleConfirm: () => {
                AppContext.showSpinner();
                Axios.dataAccess({
                    url: "system/bgt/ovrl/dpst/delete-ovrl-dpst.do",
                    methodType: "post",
                    paramType: "json",
                    paramData: {
                        ovrlMbsdsDpstDocId: ovrlMbsdsDpstDocId, // 수입결의서 문서 ID
                    },
                    onSuccessFn: () => {
                        AppContext.hideSpinner();
                        AppContext.openAlert({
                            title: "수입결의서 삭제",
                            msg: "수입결의서가 삭제되었습니다.",
                            icon: "check"
                        });
                        doReset(); // 초기화
                        setCallTime(new Date());
                    },
                });
            },
        });
    }

    return (
        <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
            {/* 타이틀 */}
            <div className="mt5">
                <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">교부금 수입결의서 관리</span>
                <div className="bdt3 bdc007bff pb10" />
            </div>

            {/* 검색 조건 */}
            <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
                <div className="dpFlx aiC flxWrap gap14">
                    {/* 본부 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">본부</span>
                        <SelectBox value={upOgnzCd}
                            width={150} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#F6F6F6" paddingRight={8}
                            codeArray={Util.getUpOrgCodeArray("01")}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setUpOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    {/* 지부 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">지부</span>
                        <SelectBox value={ognzCd}
                            width={150} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#F6F6F6" paddingRight={8}
                            codeArray={Util.getDownOrgCodeArray(upOgnzCd)}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    {/* 회계연도 */}
                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">회계연도</span>
                        <SelectBox value={acntgYear}
                            width={120} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#fff" paddingRight={8}
                            codeArray={Util.getAcntgYearArray()}
                            placeholder="선택"
                            onChangeFunc={(data: string) => setAcntgYear(data)}
                        />
                    </div>

                    {/* 진행상태 */}
                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">진행상태</span>
                        <SelectBox  value={searchAtrzSttsCd}
                            width={140} height={36}
                            fontSize={14}
                            fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#fff"
                            codeArray={[{ code: "", name: "전체" }].concat(Util.getCodeArray("UI_ATRZ_STTS_CD_01"))}
                            placeholder="전체" paddingRight={8}
                            onChangeFunc={(data: string) => setSearchAtrzSttsCd(data)}
                        />
                    </div>
                </div>
            </div>    

            {/* 좌/우 */}
            <div className="dpFlx gap14 aiFs">
                {/* LEFT */}
                <div className="w360">
                    <div className="bd1d9d9d9 bdrd6 bgcWhite p15">
                        <div className="mb10">
                            <span className="fm-b fs16 fc007bff ls1 fw700">수입결의서 입력</span>
                            <span className="fm-b fs13 ls06 fc1A5276" style={{marginLeft: "77px"}}>{dpstRsdocNo}</span>
                        </div>
                
                        {atrzSttsCd === "05" && (
                        <AtrzLineBox
                            listLine={listLine}
                            open={openLine}
                            onToggle={() => setOpenLine((v) => !v)}
                        />)}
                
                        {/* 수입일자 */}
                        <div className="dpFlx flxdCol mb10">
                            <div className="h30 dpFlx aiE pb5">
                                <span className="fm-b fs13 ls06 fc111111">수입일자</span>
                            </div>
                            <DatePickerBox value={dpstDt}
                                width={320} height={36}
                                fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                bgColor={["01", "02", "05"].includes(atrzSttsCd) ? "#F6F6F6": "white"}
                                textAlign="left"
                                calendarThemeColor={AppContext.themeColor}
                                onChange={(data: string) => setDpstDt(data)}
                                disabled={["01", "02", "05"].includes(atrzSttsCd) ? true : false}   // 작성중(01), 작성완료(02), 반려(05)
                            />
                        </div>

                        {/* 관/항/목 */}
                        <div className="dpFlx mb10">
                            <div className="dpFlx flxdCol flx1">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">관</span>
                                </div>
                                <SelectBox value={bgtSbjct1stCd}
                                    width={100} height={36}
                                    fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                    borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                    background="#f6f6f6" paddingRight={8}
                                    codeArray={Util.getBgt1CodeArray("01", ognzCd)}
                                    placeholder="선택"
                                    onChangeFunc={(v: string) => {
                                        setBgtSbjct1stCd(v);
                                        setBgtSbjct2stCd("");
                                        setBgtSbjct3stCd("");
                                    }}
                                    disabled={true}
                                />
                            </div>

                            <div className="dpFlx flxdCol flx1">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">항</span>
                                </div>
                                <SelectBox value={bgtSbjct2stCd}
                                    width={100} height={36}
                                    fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                    borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                    background="#f6f6f6" paddingRight={8}
                                    codeArray={Util.getBgt2CodeArray(bgtSbjct1stCd, ognzCd)}
                                    placeholder="선택"
                                    onChangeFunc={(v: string) => {
                                        setBgtSbjct2stCd(v);
                                        setBgtSbjct3stCd("");
                                    }}
                                    disabled={true}
                                />
                            </div>

                            <div className="dpFlx flxdCol flx1">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">목</span>
                                </div>
                                <SelectBox value={bgtSbjct3stCd}
                                    width={100} height={36}
                                    fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                    borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                    background="#fff" paddingRight={8}
                                    codeArray={Util.getBgt3CodeArray(bgtSbjct2stCd, ognzCd).filter(item => item.code === "I_003_001_002" || item.code === "I_003_001_003")} // 조합비, 무기계약직조합비만 사용
                                    placeholder="선택"
                                    onChangeFunc={(v: string) => setBgtSbjct3stCd(v)}
                                />
                            </div>
                        </div>

                        {/* 적요 */}
                        <div className="dpFlx flxdCol mb10">
                            <div className="h30 dpFlx aiE pb5">
                                <span className="fm-b fs13 ls06 fc111111">적요</span>
                            </div>
                            <TextBox value={abstrCn}
                                width={320} height={36}
                                fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                placeholder="수입 관련 적요를 입력하세요"
                                style={{ backgroundColor: "#fff", padding: "0 12px" }}
                                onChangeFunc={(t: string) => setAbstrCn(t)}
                            />
                        </div>

                        {/* 메모 */}
                        <div className="dpFlx flxdCol mb10">
                            <div className="h30 dpFlx aiE pb5">
                                <span className="fm-b fs13 ls06 fc111111">메모</span>
                            </div>
                            <TextBox value={memo}
                                width={320} height={36}
                                fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                placeholder="수입 관련 메모를 입력하세요"
                                style={{ backgroundColor: "#fff", padding: "0 12px" }}
                                onChangeFunc={(t: string) => setMemo(t)}
                            />
                        </div>

                        {/* 금액 */}
                        <div className="dpFlx flxdCol mb10">
                            <div className="h30 dpFlx aiE pb5">
                                <span className="fm-b fs13 ls06 fc111111">금액</span>
                            </div>
                            <div className="w320 h36 bgcWhite dpFlx aiC bsBdb">
                                <NumberTextBox value={dpstAmt}
                                    className="w320 h36 bd1 bdrd2"
                                    thousandsGroupStyle="thousand" thousandSeparator=","
                                    placeholder="0"
                                    fontSize={14} fontFamily="fm-c" letterSpacing={-1}
                                    onChange={(data: string) => setDpstAmt(data)}
                                    inputClass={"taR mr10"}
                                />
                            </div>
                        </div>

                        {/* 조합비 첨부파일 */}
                        <BudgetFile
                            atrzSttsCd          = {atrzSttsCd} // 결재상태코드
                            fileSeCd            = "C01"        // 파일구분코드(총괄 교부금_수입결의서[A01])
                            savedBudgetFiles    = {savedBudgetFiles}
                            setSavedBudgetFiles = {setSavedBudgetFiles}
                            budgetFiles         = {budgetFiles}
                            setBudgetFiles      = {setBudgetFiles}
                        />

                        {/* 반려 */}
                        {atrzSttsCd === "05" && (
                            <div className="dpFlx flxdCol">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">반려 사유</span>
                                </div>
                                <div className="dpFlx aiFs">
                                    <div className="flxg1 bsBdb">
                                        <textarea
                                            value={atrzOpnnCn}
                                            className="w320 h70 bsBdb p10 bd1 bdcDFDFDF bdrd2 fm-c fs14 fc333333 ls1 bgcWhite rszN"
                                            maxLength={2000}
                                            placeholder="반려 사유를 상세히 입력해주세요."
                                            onChange={(e) => setAtrzOpnnCn(e.target.value)}
                                            readOnly={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="bdb1 bdcDDDDDD mt15 mb15" />

                        {/* 버튼 */}
                        <div className="dpFlx aiC jcSb gap10">
                            <Button buttonName="초기화"
                                width={80} height={40}
                                fontFamily="fm-b" fontSize={16} letterSpacing={-1}
                                variant="styInitBtn" borderRadius={6}
                                buttonClass="dpFlx aiC jcC"
                                onClickFunc={() => doReset()}
                            />

                            {(atrzSttsCd === "01" || atrzSttsCd === "02" || atrzSttsCd === "05") &&
                                <Button buttonName="삭제"
                                    width={75} height={40}
                                    fontFamily="fm-b" fontSize={15} letterSpacing={-1}
                                    variant="styDeleteBtn" borderRadius={6}
                                    buttonClass="dpFlx aiC jcC"
                                    onClickFunc={doDelete}
                                />
                            }

                            {(atrzSttsCd === "00" || atrzSttsCd === "01") &&
                                <Button buttonName="임시저장"
                                    width={100} height={40}
                                    fontFamily="fm-b" fontSize={16} letterSpacing={-1}
                                    variant="styTempBtn" borderRadius={6}
                                    buttonClass="dpFlx aiC jcC"
                                    onClickFunc={() => doSave("01")}
                                />
                            }

                            <Button buttonName="작성완료"
                                width={100} height={40}
                                fontFamily="fm-b" fontSize={16} letterSpacing={-1}
                                variant="styCompleteBtn" borderRadius={6}
                                buttonClass="dpFlx aiC jcC"
                                onClickFunc={() => doSave("02")}
                            />
                        </div>
                    </div>
                </div>

                {/* RIGHT 수입결의서 목록 */}
                <div className="flx1">
                    <BgtOvrlDpstMngList
                        ognzCd                = {ognzCd}
                        acntgYear             = {acntgYear}
                        searchAtrzSttsCd      = {searchAtrzSttsCd}
                        setOvrlMbsdsDpstDocId = {setOvrlMbsdsDpstDocId}
                        callTime              = {callTime}
                    ></BgtOvrlDpstMngList>
                </div>
            </div>    
        </div>
    );
};

export default BgtOvrlDpstMng;
